import {Component, ElementRef, inject, Input, OnInit, ViewChild, ViewContainerRef} from "@angular/core";
import {ChartDataset} from "chart.js";
import {
    AggregatedDataPoint,
    AlertType,
    BuildingType,
    Connection,
    ConnectionType,
    DataType,
    MeasurementsResult,
    MeterType,
    TimeRange
} from "@flowmaps/flowmaps-typescriptmodels";
import {ChartDataPerMeasurement, ChartDataProvider, DashboardTime, Slot} from "../../utils/chart-data-provider";
import {ChartType, TooltipModel} from "chart.js/dist/types";
import {View} from "../../common/view";
import {Handler} from "../../common/handler";
import {ChartModalOptions, ChartUtilsService} from "./chart-utils.service";
import {AppContext, CompletenessInfo} from "../../app-context";
import {PaginationComponent} from "../../common/pagination/pagination.component";
import {ChartCompare, ChartOptions, ChartOptionType} from "../dashboard/dashboard.types";
import {
    DataQueryFilters,
    MeasurementsHandlerOpenModalCommand
} from "../measurements-component/measurements-handler.component";
import {SourceInfo} from "../../utils/source-providers/sources-provider";
import {interpolate, lodash, nonNull} from "../../common/utils";
import {EntityType} from "../../handlers/entity";
import {Observable} from "rxjs";
import {sendCommandAndForget} from "../../common/app-common-utils";
import {DashboardContext} from "../dashboard/dashboard.context";
import {TranslateDirective} from "../../common/utils/translate.directive";
import moment from "moment/moment";
import {cloneDeep, groupBy} from "lodash";
import {Rgba} from "../../common/rgba";
import {Measurements} from "../measurements-component/measurements";
import {RefdataUtils} from "../refdata/refdata-utils";
import {filter} from "rxjs/operators";

@Component({
    template: "",
})
@Handler()
export class BaseMeasurementChartComponent extends View implements OnInit {
    chartUtils = inject(ChartUtilsService);
    appContext = AppContext;
    @Input() showInReport: boolean;

    @ViewChild('container', {read: ViewContainerRef}) container: ViewContainerRef;
    @ViewChild("pagination") pagination: PaginationComponent<any>;

    completenessInfo: ChartCompleteness;
    chartOptionType: ChartOptionType;
    mainConsumptionType: DataType;
    mainConnectionType: ConnectionType;
    connectionTypes: ConnectionType[] = [];

    chartDataProvider: ChartDataProvider = new ChartDataProvider();
    tableElement: ElementRef;
    options: ChartOptions;
    modalOptions: ChartModalOptions = {
        fullScreen: false,
    }
    isPortfolio: boolean;
    filters: DataQueryFilters;
    timeRange: DashboardTime;
    comparedTimeRange: DashboardTime;
    selectedConnections: SourceInfo[] = [];
    feedInPowerTypes: DataType[] = [];
    contractedCapacityRequiredForPower: boolean;
    hasProductionMeter: boolean;
    groupByEntityId: boolean;
    onlyMetersSelected: boolean;

    _data: MeasurementDataset[];
    _rawData: ChartDataPerMeasurement;
    _rawPreviousData: ChartDataPerMeasurement;

    private uniqueId = lodash.uniqueId();
    getInputId = (id: string) => `${this.uniqueId}-${id}`;

    @Input()
    set data(d: ChartModalOptions) {
        this.modalOptions = d;
    }

    ngOnInit() {
        this.subscribeTo("getChartOptions", this.chartOptionType).subscribe((opts: ChartOptions) => {
            opts.showAllDays = opts.showAllDays === undefined ? true : opts.showAllDays;
            opts.selectedDataType = opts.selectedDataType || this.mainConsumptionType;
            opts.selectedDays = opts.selectedDays || [];
            opts.showIncidents = false
            opts.selectedIncidents = []
            this.options = lodash.merge(this.options, opts);
        });
        this.subscribeTo("isPortfolio").subscribe(isPortfolio => {
            this.isPortfolio = isPortfolio;
            this.sendQuery("groupByEntity").subscribe(groupByEntity => {
                this.groupByEntityId = groupByEntity;
                if (!this.groupByEntityId && groupByEntity && this.chartOptionType != ChartOptionType.Water) {
                    this.options.groupByEntityId = true;
                }
            });
        });
        this.subscribeTo("getChartDataQueryFilters")
            .pipe(filter(f => !DashboardContext.hasSelectedSources() || (f.allSelectedSources && f.allSelectedSources.length > 0)))
            .subscribe((f: DataQueryFilters) => this.refreshAfterFilterChange(f));
    }

    private refreshAfterFilterChange(f: DataQueryFilters) {
        this.filters = f;
        this.timeRange = f.timeRange;
        if (f.timeRange) {
            if (f.allSelectedSources?.length) {
                const allSelectedSources = f.allSelectedSources || [];
                this.selectedConnections = allSelectedSources.filter(s => this.connectionTypes.includes(s.connectionType) && s.type === EntityType.connection);
                this.hasProductionMeter = allSelectedSources.some(s => s.type === EntityType.meter && s.meterType === MeterType.GROSS_PRODUCTION);
                this.onlyMetersSelected = allSelectedSources.every(s => s.type === EntityType.meter) ||
                    (allSelectedSources.length === 1 && allSelectedSources[0].type === EntityType.connection && allSelectedSources[0].source.connection.meters.some(m => m.info.type == MeterType.INTERMEDIATE));
                this.options.groupByEntityIdPossible = allSelectedSources.some(s => s.type === EntityType.meter
                    && MeterType.INTERMEDIATE === s.meterType
                    && this.connectionTypes.includes((<Connection>s.source.connection).info.connectionType as ConnectionType));
                if (this.onlyMetersSelected) {
                    this.groupByEntityId = this.options.groupByEntityId = true;
                }
            }
            this.getChartData(f.timeRange).subscribe((d: ChartDataPerMeasurement) => this.setData(d, f.timeRange));
            this.refreshComparedData();
        }
    }

    private getChartData = (timeRange?: DashboardTime): Observable<ChartDataPerMeasurement> => {
        let type = !this.showInReport && (!DashboardContext.hasSelectedSources() && !this.filters.sources?.length)
            ? "getTotals" : "getMeasurementsData";
        return this.sendQuery(type, { range: timeRange, showIncidents: this.options.showIncidents });
    };

    private getCompleteness = (timeRange?: DashboardTime): Observable<CompletenessInfo[]> => {
        return this.sendQuery("getCompleteness", timeRange);
    }

    openModal(type: typeof BaseMeasurementChartComponent) {
        this.sendCommandAndForget("openChartInModal", <MeasurementsHandlerOpenModalCommand>{
            component: type,
            chartOptions: {
                [this.chartOptionType]: this.options
            },
            chartModalData: this.modalOptions,
            container: this.container,
            modalClasses: "modal-dialog-centered modal-xl d-flex align-items-stretch"
        });
    }

    closeModal = () => sendCommandAndForget("closeModal");

    getSelectedEans = (): string[] =>
        this.getSelectedConnections().filter(c => c.source.connection.meters &&
                c.source.connection.meters.some(m => m.info.type == MeterType.PRIMARY))
            .map(c => c.source.connection.info.code);


    getSelectedConnections = (connectionType?: ConnectionType) => connectionType
        ? this.selectedConnections.filter(s => s.connectionType === connectionType) : this.selectedConnections;

    contractedCapacity(connectionType: ConnectionType) {
        const connections = this.getSelectedConnections(connectionType);
        if (this.powerAllowed(connectionType)) {
            if (this.isPortfolio) {
                return connections.length === 1 ? connections[0]?.source.connection.contractedCapacity : null;
            } else {
                return connections.find(c => c.source.connection.info.code === this.showPowerForEan(connectionType))?.source.connection.contractedCapacity;
            }
        }
        return null;
    }

    measurementUnit = () => DashboardContext.getMeasurementUnit(this.mainConsumptionType);

    get powerAxisTitle(): string {
        const hasWeatherSelected = this.options.selectedWeatherTypes?.length > 0;
        const hasPowerSelected = this.selectedConnections.filter(con => con.source.connection.meters.some(m => m.info.type == MeterType.PRIMARY)).length === 1 && this.options.showPower;
        const connectionType = this.getSelectedConnections()[0]?.connectionType;
        return !hasWeatherSelected && hasPowerSelected
            ? DashboardContext.getMeasurementUnit(this.powerDataTypeMapped()[connectionType]) : null;
    }

    get y2AxisTitle(): string {
        return this.options.selectedWeatherTypes?.length === 1
            ? DashboardContext.getMeasurementUnit(this.options.selectedWeatherTypes[0]) : null;
    }

    hasDataOfTypes = (dataType: DataType | DataType[]): boolean => {
        const dataTypes = lodash.isArray(dataType) ? dataType : [dataType];
        return [this._rawData?.measurements, this._rawData?.estimatedMeasurements,
            this._rawPreviousData?.measurements, this._rawPreviousData?.estimatedMeasurements].filter(nonNull)
            .some(d => Object.keys(d).some(k => dataTypes.includes(k as DataType)));
    }

    powerAllowed = (connectionType: ConnectionType): boolean => this.isPortfolio
        ? this.getSelectedConnections(connectionType).length === 1 : !!this.showPowerForEan(connectionType);

    hasPower = (connectionType?: ConnectionType): boolean => connectionType
        ? this.hasDataOfTypes(this.powerDataTypeMapped()[connectionType])
        : Object.entries(this.powerDataTypeMapped())
            .some(e => this.hasDataOfTypes(e[1]));

    getDataFromMeasurements = (data: { [P in DataType]?: MeasurementDataset[] }): MeasurementDataset[] => {
        return Object.values(data).flatMap(v => v).filter(v => v);
    }

    refreshData = (emit?: boolean) => {
        this._data = [];
        if (this._rawData) {
            this._data = this.getConsumptionAndProductionData(this.getDataFromMeasurements(this._rawData.measurements))
                .concat(this.getConsumptionAndProductionData(this.getDataFromMeasurements(this._rawData.estimatedMeasurements)));
            if (this.options.showCompleteness && this._rawData?.completeness) {
                this._data = this._data.concat(this.getCompletenessAxis(this._rawData.completeness));
            }
            this._data = this._data.concat(this.options.selectedWeatherTypes.map(dataType => this.getWeatherDataset(dataType, this.timeRange, false)));
            if (this._rawData.incidents?.length) {
                this._data = this._data.concat(this.createIncidentsDatasets(this._rawData));
            }
        }
        if (this._rawPreviousData) {
            this._data = this._data.concat(this.getConsumptionAndProductionData(this.getDataFromMeasurements(this._rawPreviousData.measurements)))
                .concat(this.getConsumptionAndProductionData(this.getDataFromMeasurements(this._rawPreviousData.estimatedMeasurements)));
            if (this.options.showCompleteness && this._rawPreviousData?.completeness) {
                this._data = this._data.concat(this.getCompletenessAxis(this._rawPreviousData.completeness, true,
                    ChartUtilsService.getComparedYear(this.options.compare, this.timeRange)));
            }
            this._data = this._data.concat(this.options.selectedWeatherTypes.map(dataType => this.getWeatherDataset(dataType, this.comparedTimeRange, true)));
        }
        this._data = this._data.concat(this.addConnectionTypeSpecificData(this.timeRange));
        this.updateCompleteness();
        if (emit) {
            const yAxisDataTypes = this.measurementTypes().concat(this.productionDataTypes());
            this.chartDataProvider.emit({
                datasets: this._data.filter(d =>
                    !yAxisDataTypes.includes(d.measurementType) || d.dataset.yAxisID === this.getYAxis()),
                labels: Measurements.getSlots(this.timeRange).map(s => s.label)
            });
        }
    }

    createIncidentsDatasets(chartData: ChartDataPerMeasurement): any[] {
        const datasets: MeasurementDataset<'scatter'>[] = [];
        const slots: Slot[] = Measurements.getSlots(this.timeRange);
        const incidentIcon = new Image();
        incidentIcon.src = '/assets/images/warning-triangle.svg';
        const incidents = this.getRelevantIncidentEntries(chartData)
            .map(({entity, alertType, dataPoints}) => ({
                entity,
                alertType,
                dataPoints: this.filterDataPointsWithinRange(dataPoints, {
                    start: moment(this.timeRange.start).valueOf(),
                    end: moment(this.timeRange.end).valueOf()
                })
            }))
            .filter(incident =>
                incident.dataPoints.length > 0 &&
                (this.options.showGrossProduction || incident.entity.meter.info.type !== 'GROSS_PRODUCTION') &&
                (this.onlyMetersSelected || incident.entity.meter.info.type !== 'INTERMEDIATE')
            );

        const incidentsPerSlot = slots.map(slot => {
            const slotRange = {start: slot.startTime, end: slot.endTime};
            return incidents.flatMap(({entity, alertType, dataPoints}) =>
                this.filterDataPointsWithinRange(dataPoints, slotRange).map(dp =>
                    ({entity, alertType, timeRange: dp.timeRange})));
        });
        const accumulatedValues = this.getAccumulatedData(this.groupByEntityId ? chartData.byEntity : chartData.totals, this.timeRange, true);
        const consumptionValues = this.getAccumulatedData(this.groupByEntityId ? chartData.byEntity : chartData.totals, this.timeRange, false);
        const offset = accumulatedValues.length > 0 ? Math.max(...accumulatedValues) * 0.1 : 0;

        slots.forEach((slot, slotIndex) => {
            const maxIncidentsPerSlot = 10;
            const slotMax = consumptionValues[slotIndex] ?? 0;
            const yValue = slotMax + offset;
            const incidentsInSlot = incidentsPerSlot[slotIndex];
            const incidentsToShow = incidentsInSlot.length <= maxIncidentsPerSlot
                ? incidentsInSlot
                : (() => {
                    const grouped = groupBy(incidentsInSlot, a => a.alertType);
                    const contractedPower = (grouped['ContractedPowerExceeded'] ?? []).slice(0, 4);
                    const contractedFeedInPower = (grouped['ContractedFeedInPowerExceeded'] ?? []).slice(0, 1);
                    const disconnected = (grouped['Disconnected'] ?? []).slice(0, 4);
                    const selected = [...disconnected, ...contractedPower, ...contractedFeedInPower];
                    const missingData = (grouped['MissingData'] ?? [])
                        .slice(0, maxIncidentsPerSlot - selected.length);
                    return [...selected, ...missingData];
                })();
            const hidden = incidentsInSlot.length - incidentsToShow.length;
            incidentsToShow.forEach(({alertType, entity }) => {
                const data = [{x: slot.label, y: yValue,  entity}];
                const tooltipLabels = [slot.label];
                const color = AppContext.getAlertConfig(alertType as AlertType).color;
                datasets.push({
                    measurementType: alertType as any,
                    entityId: entity.meter.meterId,
                    estimated: false,
                    dataset: {
                        label: TranslateDirective.getTranslation(alertType.toLowerCase()),
                        data,
                        type: 'scatter',
                        yAxisID: 'yScatter',
                        fill: false,
                        spanGaps: true,
                        order: AppContext.indexOfDataType(alertType as DataType),
                        measurementType: alertType as any,
                        tooltipLabels,
                        backgroundColor: color,
                        tooltip: {
                            formatter: () => `${RefdataUtils.addressFormatter(entity.location.info.address, false)} - ${entity.connection.info.code} (${entity.connection.info.surveyor.name})`,
                            borderColors: [color],
                            stack: "Alerts",
                        },
                        stack: "Current period",
                        pointStyle: incidentIcon,
                        pointRadius: 10,
                        pointHoverRadius: 10,
                        pointHitRadius: 0,
                        showLine: false,
                        clip: false,
                        ...(hidden ? {
                            footerText: TranslateDirective.getTranslation("incidentfooter", "", { count: hidden })
                        } : {})

                    } as any
                });
            });
        });
        return datasets;
    }

    private getRelevantIncidentEntries(chartData: ChartDataPerMeasurement) {
        return chartData.incidents
            .filter(a => this.connectionTypes.includes(a.entity?.connection?.info.connectionType as ConnectionType))
            .flatMap(incident => {
                if (!incident.incidents) return [];
                return Object.entries(incident.incidents)
                    .filter(([alertType, dataPoints]) =>
                        dataPoints && dataPoints.length > 0 &&
                        this.options.selectedIncidents?.some(option => option.value === alertType))
                    .map(([alertType, dataPoints]) =>
                        ({entity: incident.entity, alertType, dataPoints}));
            });
    }

    private filterDataPointsWithinRange(dataPoints: AggregatedDataPoint[], range: { start: number, end: number }): AggregatedDataPoint[] {
        return dataPoints.filter(dp => {
            const dpStart = moment(dp.timeRange?.start).valueOf();
            return dpStart >= range.start && dpStart < range.end;
        });
    }

    protected getAccumulatedData(data: MeasurementsResult[], dateRange: DashboardTime, withProductionData: boolean): number[] {
        const relatedTypes = [
            ...this.measurementTypes(),
            ...(withProductionData ? Object.values(this.productionDataTypesMapped()).flat() : [])
        ];
        const merged: MeasurementsResult = {
            measurements: {},
            estimatedMeasurements: {}
        };
        for (const result of data) {
            for (const type of relatedTypes) {
                let m = result.measurements?.[type] ?? [];
                let e = result.estimatedMeasurements?.[type] ?? [];
                if (type === DataType.heatConsumption) {
                    m = m.map(dp => ({ ...dp, value: dp.value * DashboardContext.gjToM3Rate }));
                    e = e.map(dp => ({ ...dp, value: dp.value * DashboardContext.gjToM3Rate }));
                }
                if (m.length > 0) {
                    merged.measurements![type] = (merged.measurements![type] ?? []).concat(m);
                }
                if (e.length > 0) {
                    merged.estimatedMeasurements![type] = (merged.estimatedMeasurements![type] ?? []).concat(e);
                }
            }
        }
        const allDataPoints: AggregatedDataPoint[] = relatedTypes.flatMap(type => [
            ...(merged.measurements?.[type] ?? []),
            ...(merged.estimatedMeasurements?.[type] ?? [])
        ]);
        const chartData = Measurements.dataPointsToChartData(allDataPoints);
        const grouped = Measurements.groupByTimeFrames(chartData, dateRange, AppContext.getAggregationMethod(this.mainConsumptionType));
        return grouped.data;
    }

    incidentOptions: IncidentOption[] = INCIDENT_OPTIONS;

    incidentsChange(selected: IncidentOption[]) {
        this.options.selectedIncidents = selected;
        this.options.showIncidents = selected.length > 0;
        this.refreshData(true);
    }

    showIncidentsChange($event: any) {
        this.options.showIncidents = $event;
        this.options.selectedIncidents = !$event ? [] : this.incidentOptions
        if ($event && !this._rawData.incidents?.length) {
            this.getChartData(this.timeRange).subscribe((d: ChartDataPerMeasurement) => this.setData(d, this.timeRange));
        }
        this.refreshData(true);
    }

    private updateCompleteness = () => {
        this.completenessInfo = this._rawData?.completeness ? {
            title: AppContext.completenessTitle(this._rawData.completeness, this.connectionTypes),
            color: AppContext.completenessColor(this._rawData.completeness, this.connectionTypes),
            completeness: AppContext.completeness(this._rawData.completeness, this.connectionTypes)
        } : null;
    }

    private addConnectionTypeSpecificData(timeRange: DashboardTime): MeasurementDataset[] {
        let datasets: MeasurementDataset[] = [];
        datasets = this.connectionTypes.flatMap(c => this.addPowerData(datasets, timeRange, c))
            .filter(a => a);
        return datasets;
    }

    private addPowerData(datasets: MeasurementDataset[], timeRange: DashboardTime, connectionType: ConnectionType) {
        const contractedCapacity = this.contractedCapacity(connectionType);
        const contractedRequired = this.contractedCapacityRequiredForPower;
        if ((contractedRequired && contractedCapacity) || !contractedRequired) {
            datasets = datasets.concat(this.addPowerToChart(timeRange, connectionType));
        }
        return datasets;
    }

    addPowerToChart(timeRange: DashboardTime, connectionType: ConnectionType): MeasurementDataset[] {
        const datasets: MeasurementDataset[] = [];
        const powerType = this.powerDataTypeMapped()[connectionType];
        if (this.options.showPower && this.powerAllowed(connectionType) && powerType) {
            if (this.hasPower(connectionType)) {
                datasets.push(this.getPowerDataset(powerType));
            }
            const contractedCapacity = this.contractedCapacity(connectionType);
            if (contractedCapacity) {
                const contractedCapacityColor = "#a2a2a2";
                datasets.push({
                    dataset: {
                        type: "line",
                        borderDash: [5],
                        order: AppContext.indexOfDataType(powerType),
                        yAxisID: "power",
                        spanGaps: false,
                        label: TranslateDirective.getTranslation("Contracted power", true),
                        borderColor: contractedCapacityColor,
                        backgroundColor: contractedCapacityColor,
                        pointBorderColor: contractedCapacityColor,
                        data: Measurements.getSlots(timeRange).map(i =>
                            this.feedInPowerTypes.includes(powerType) ? -contractedCapacity : contractedCapacity),
                        stack: `contracted-capacity-${powerType}`,
                        tooltip: {
                            formatter: this.chartUtils.getCustomTooltipFormatter(powerType),
                            stack: AppContext.measurementName(powerType),
                            valuesInverted: this.feedInPowerTypes.includes(powerType)
                        }
                    }
                });
            }
        }
        return datasets;
    }

    private getPowerDataset(measurementType: DataType): MeasurementDataset {
        const color = DashboardContext.getMeasurementColor(DashboardContext.stacks.currentPeriod, measurementType);
        return {
            measurementType: measurementType,
            dataset: {
                type: "line",
                borderDash: [5],
                order: AppContext.indexOfDataType(measurementType),
                yAxisID: "power",
                spanGaps: false,
                borderColor: color,
                backgroundColor: color,
                pointBorderColor: color,
                label: AppContext.measurementName(measurementType),
                data: this.getPowerData(measurementType),
                stack: AppContext.measurementName(measurementType),
                tooltip: {
                    formatter: this.chartUtils.getCustomTooltipFormatter(measurementType),
                    valuesInverted: this.feedInPowerTypes.includes(measurementType)
                }
            }
        };
    }

    getPowerData(powerDataType: DataType) {
        let powerData: number[] = this.getChartDataForMeasurement(powerDataType, this.timeRange, false).data;
        if (this.feedInPowerTypes.includes(powerDataType)) {
            powerData = powerData.map(d => -d);
        }
        return powerData;
    }

    private setCompareData(data: ChartDataPerMeasurement, timeRange: DashboardTime, emitData: boolean = true) {
        this._rawPreviousData = null;
        if (data?.totals) {
            this.getCompleteness(timeRange).subscribe(c => {
                this._rawPreviousData = {
                    ...data,
                    measurements: this.groupDataByMeasurement(this.groupByEntityId ? data.byEntity : data.totals, timeRange, DashboardContext.stacks.lastYear, ChartUtilsService.getComparedYear(this.options.compare, this.timeRange), false, (m) => m.measurements),
                    estimatedMeasurements: this.groupDataByMeasurement(this.groupByEntityId ? data.byEntity : data.totals, timeRange, DashboardContext.stacks.lastYear, ChartUtilsService.getComparedYear(this.options.compare, this.timeRange), true, (m) => m.estimatedMeasurements),
                    completeness: c
                };
                this.refreshData(emitData);
            });
        } else {
            this.refreshData(emitData);
        }
    }

    private setData(data: ChartDataPerMeasurement, timeRange: DashboardTime, emitData: boolean = true) {
        this._rawData = null;
        if (data.totals) {
            this.getCompleteness(timeRange).subscribe(c => {
                this._rawData = {
                    ...data,
                    measurements: this.groupDataByMeasurement(this.groupByEntityId ? data.byEntity : data.totals, timeRange, DashboardContext.stacks.currentPeriod, null, false, (m) => m.measurements),
                    estimatedMeasurements: this.groupDataByMeasurement(this.groupByEntityId ? data.byEntity : data.totals, timeRange, DashboardContext.stacks.currentPeriod, null, true, (m) => m.estimatedMeasurements),
                    completeness: c
                };
                this.refreshData(emitData);
            });
        } else {
            this.refreshData(emitData);
        }
    }

    showFeedIn = (): boolean => this.options.showFeedIn || this.options.showFeedIn === undefined;
    showConsumption = (): boolean => this.options.showConsumption || this.options.showConsumption === undefined;
    isDataTypeSelected = (type: DataType): boolean => this.options.selectedWeatherTypes.includes(type);

    productionDataTypes = (): DataType[] => [];

    compareFormatter = (c: ChartCompare) => c.relative
        ? (c.comparedYear === 1
            ? TranslateDirective.getTranslation("Last year", true)
            : `${c.comparedYear} ${TranslateDirective.getTranslation("years ago", true)}`)
        : `${c.comparedYear}`;

    compareEquals = (c: ChartCompare, o: ChartCompare) => c && o && c.relative === o.relative && c.comparedYear === o.comparedYear;
    getWeatherInputId = (weatherType: DataType) => this.getInputId('checkbox-' + weatherType + '-' + this.chartOptionType);

    compareYearOptions: ChartCompare[] = [{
        comparedYear: 1,
        enabled: true,
        relative: true
    }].concat(AppContext.getYearsFrom(moment().year() - DashboardContext.maximumYears).map(y => ({
        comparedYear: y,
        enabled: true,
        relative: false
    })).reverse());

    toggleWeather(type: DataType) {
        const options = this.options;
        if (options.selectedWeatherTypes.includes(type)) {
            options.selectedWeatherTypes = options.selectedWeatherTypes.filter(t => t !== type);
        } else {
            options.selectedWeatherTypes.push(type);
        }
        this.refreshData(true);
    }

    compareChange(compare: boolean) {
        this.options.compare.enabled = compare;
        if (!this.options.compare.comparedYear) {
            this.options.compare.comparedYear = 1;
            this.options.compare.relative = true;
        }
        this.refreshComparedData();
        if (!this.options.compare.enabled) {
            this.setCompareData(null, null, true);
        }
    }

    comparedYearChange(compareInfo: ChartCompare) {
        this.options.compare = compareInfo
        this.options.compare.enabled = true;
        this.refreshComparedData();
        if (!this.options.compare.enabled) {
            this.setCompareData(null, null,true);
        }
    }

    private refreshComparedData = () => {
        if (this.options.compare.enabled) {
            this.comparedTimeRange = ChartUtilsService.getForYear(this.timeRange, ChartUtilsService.getComparedYear(this.options.compare, this.timeRange));
            this.comparedTimeRange.resolution = AppContext.resolutionForTimeRange(this.comparedTimeRange);
            this.getChartData(this.comparedTimeRange).subscribe(d => this.setCompareData(d, this.comparedTimeRange, true));
        }
    }

    groupByEntityIdChange(groupByEntityId: boolean) {
        this.options.groupByEntityId = groupByEntityId;
        this.options.splitOffPeak = !groupByEntityId;
        this.refreshData(true);
    }

    showGrossProductionChange(showGrossProduction: boolean) {
        this.options.showGrossProduction = showGrossProduction;
        this.refreshData(true);
    }

    showCompletenessChange(showCompleteness: boolean) {
        this.options.showCompleteness = showCompleteness;
        this.refreshData(true);
    }

    showPowerChange(showPower: boolean) {
        this.options.showPower = showPower;
        this.options.showPowerForEan = null;
        this.refreshData(true);
    }

    showPowerForEanChange(ean: string) {
        this.options.showPower = true;
        this.options.showPowerForEan = ean;
        this.refreshData(true);
    }

    showPowerForEan(connectionType: ConnectionType): string {
        if (this.isPortfolio) {
            return null;
        }
        const connections = this.getSelectedConnections(connectionType).filter(connection => {
            const meters = connection.source.connection.meters;
            return meters && meters.some(meter => meter.info.type === MeterType.PRIMARY);
        });
        if (connections.length === 1) {
            return this.options.showPower ? connections[0].source.connection.info.code : null
        }
        return this.options.showPowerForEan;
    }

    groupByEntityIdEnabled = (): boolean => this.options.groupByEntityId && this.options.groupByEntityIdPossible && this.groupByEntityId;

    toggleShowAsTable() {
        this.options.showAsTable = !this.options.showAsTable;
        this.refreshData(true)
    }

    measurementTypes = (): DataType[] => [];

    measurementIntermediateLink = (): Map<DataType, DataType[]> => new Map<DataType, DataType[]>();

    consumptionProductionLink = (): Map<DataType, DataType> => new Map<DataType, DataType>();

    measurementTypesMapped = (): { [key: string]: DataType[] } => ({});

    productionDataTypesMapped = (): { [key: string]: DataType[] } => ({})

    powerDataTypeMapped = (): { [key: string]: DataType } => {
        return {
            [ConnectionType.Electricity]: DataType.electricityPower,
            [ConnectionType.Gas]: DataType.gasPower,
            [ConnectionType.Heat]: DataType.heatPower,
            [ConnectionType.Water]: DataType.waterPower,
            [ConnectionType.ChargePoint]: DataType.chargePointPower,
            [ConnectionType.Cooling]: DataType.coolingPower
        };
    }

    private getConsumptionAndProductionData(rawData: MeasurementDataset[]): MeasurementDataset[] {
        return this.groupByEntityIdEnabled()
            ? Object.entries(lodash.groupBy(rawData, r => r.entityId))
                .flatMap(s => this.createDatasets(s[1], rawData))
            : this.createDatasets(rawData, rawData);
    }

    private createDatasets(dataset: MeasurementDataset[], rawData: MeasurementDataset[]) {
        let dataOfMeasurements = this.getDataOfMeasurements(dataset, this.measurementTypes());
        if (this.groupByEntityIdEnabled()) {
            dataOfMeasurements = this.getAggregatedConsumptionData(dataOfMeasurements, "y1", true);
        }
        const consumptionData = this.processConsumptionData(dataOfMeasurements, rawData);
        const prodData = this.processProductionData(this.getDataOfMeasurements(dataset, this.productionDataTypes()));
        const aggregatedProdData = this.getAggregatedProductionData(prodData, "y1");
        if (this.measurementTypes().includes(DataType.chargePointConsumption) && this.options.showAsTable) {
            return this.applyProductionData(consumptionData, prodData);
        }
        return this.applyProductionData(consumptionData, prodData)
            .concat(prodData)
            .concat(this.applyProductionData(this.getAggregatedConsumptionData(consumptionData, "y1"), aggregatedProdData))
            .concat(aggregatedProdData);
    }

    getDataOfMeasurements = (rawData: MeasurementDataset[], measurementTypes: DataType[]): MeasurementDataset[] =>
        rawData.filter(d => measurementTypes.includes(d.measurementType))
            .map(d => {
                const measurementSet = cloneDeep(d);
                if (measurementSet.measurementType === DataType.heatConsumption) {
                    measurementSet.dataset.data = measurementSet.dataset.data.map(v => (v as number) * DashboardContext.gjToM3Rate);
                }
                return measurementSet;
            });

    private processConsumptionData(consumptionData: MeasurementDataset[], allData: MeasurementDataset[]) {
        const createMainConsumptionSet = (parentDataset: MeasurementDataset, childDatasets: MeasurementDataset[]): MeasurementDataset => {
            parentDataset = cloneDeep(parentDataset);
            parentDataset.dataset.order = 10000;
            parentDataset.dataset.tooltip.data = cloneDeep(parentDataset.dataset.data as number[]);
            parentDataset.dataset.tooltip.formatter = this.mainConsumptionSetTooltipFormatter(parentDataset);
            this.applyDiagonalPattern(parentDataset, parentDataset.dataset.backgroundColor as string, 'rtl');
            childDatasets.forEach(d => {
                d.dataset.data.forEach((d, i) => {
                    parentDataset.dataset.data[i] = Math.max((parentDataset.dataset.data[i] as number) - (d as number), 0);
                });
            });
            return parentDataset;
        }
        if (this.groupByEntityIdEnabled()) {
            const link = this.measurementIntermediateLink();
            const hasMeasurementLink = consumptionData.some(c => link.has(c.measurementType));
            const hasParentMeterSelected = consumptionData.some(c => c.isParentMeterData);
            let data: MeasurementDataset[] = [];
            const consumptionDataAggregated = this.getAggregatedConsumptionData(consumptionData, "y1", true)
                .concat(consumptionData.filter(c => c.dataset.yAxisID === "y1Aggregated"));
            if (hasMeasurementLink) { // Measurement linking is done by Consumption - intermediate consumption
                link.forEach((value, key) => {
                    const intermediateConsumptions = allData.filter(m => value.includes(m.measurementType));
                    let mainConsumptionSet = consumptionDataAggregated.find(c => c.measurementType === key);
                    if (mainConsumptionSet) {
                        data = data.concat(createMainConsumptionSet(mainConsumptionSet, intermediateConsumptions));
                    }
                });
                return data;
            }
            if (hasParentMeterSelected) { // Parent meters is done by entity where all children have been selected. This is values - values
                const intermediateConsumptions = allData.filter(m => !m.isParentMeterData);
                let mainConsumptionSet = consumptionDataAggregated.find(c => c.isParentMeterData);
                if (mainConsumptionSet) {
                    data = data.concat(createMainConsumptionSet(mainConsumptionSet, intermediateConsumptions));
                }
                return data;
            }
        }
        return consumptionData;
    }

    private applyDiagonalPattern(d: MeasurementDataset, color: string = d.dataset.backgroundColor as string, direction: 'ltr' | 'rtl' = 'ltr') {
        d.dataset.backgroundColor = ChartUtilsService.createDiagonalPattern(color, direction);
        d.dataset.borderColor = color;
        d.dataset.borderWidth = 1;
    }

    private processProductionData(productionData: MeasurementDataset[]) {
        return productionData.map(p => {
            const d = cloneDeep(p);
            if (this.options.nettedConsumption) {
                this.applyDiagonalPattern(d, "#adadad");
                d.dataset.tooltip.valuesInverted = false;
            } else {
                d.dataset.data = d.dataset.data.map(r => -r);
            }
            return d;
        });
    }

    private applyProductionData(dataset: MeasurementDataset[], productionDataset: MeasurementDataset[]): MeasurementDataset[] {
        if (!this.options.nettedConsumption) {
            return dataset;
        }
        return cloneDeep(dataset).map(d => {
            const productionType = this.consumptionProductionLink().get(d.measurementType);
            if (productionType) {
                const prodDataset = productionDataset.find(s => s.measurementType === productionType);
                if (prodDataset) {
                    d.dataset.data = d.dataset.data.map((d, i) => (d as number) - (prodDataset.dataset.data[i] as number));
                    d.dataset.label = d.measurementType === DataType.electricityConsumption ? "Net consumption" : "Net consumption off-peak";
                }
            }
            return d;
        })
    }

    private getAggregatedConsumptionData(dataset: MeasurementDataset[], axis: string, replace: boolean = false) {
        const mappedData = this.measurementTypesMapped();
        return Object.entries(mappedData).flatMap(e => {
            return this.getAggregatedData(dataset.filter(d => e[1].includes(d.measurementType)), axis, e[0] as DataType, replace);
        });
    }

    private getAggregatedProductionData(dataset: MeasurementDataset[], axis: string, replace: boolean = false) {
        const mappedData = this.productionDataTypesMapped();
        return Object.entries(mappedData).flatMap(e => {
            return this.getAggregatedData(dataset.filter(d => e[1].includes(d.measurementType)), axis, e[0] as DataType, replace);
        });
    }

    private getAggregatedData(dataset: MeasurementDataset[], axis: string, m: DataType, replace: boolean = false): MeasurementDataset[] {
        const axisData = dataset.filter(d => d && d.dataset["yAxisID"] === axis);
        if (axisData.length && axis === "y1" && (this.getYAxis() !== axis || replace)) {
            var obj = axisData.find(a => a.measurementType === m) || axisData[0]
            const aggregatedData = cloneDeep({
                ...obj,
                dataset: {
                    ...obj.dataset,
                    label: this.getLabel(m, this.groupByEntityIdEnabled() ? obj.entityId : null)
                }
            });
            if (!this.groupByEntityIdEnabled()) {
                delete aggregatedData.entityId;
            }
            aggregatedData.dataset["yAxisID"] = this.getYAxis();
            aggregatedData.dataset.data = aggregatedData.dataset.data.map((r, i) =>
                lodash.sum(axisData.flatMap(r => r.dataset.data[i])));
            return [aggregatedData];
        }
        return [];
    }

    protected getYAxis() {
        return "y1";
    }

    private getCompletenessAxis = (completeness: CompletenessInfo[], isPrevious: boolean = false, comparedYear: number = null): MeasurementDataset => {
        let color = Rgba.fromString("rgba(163, 98, 201, 100)");
        if (isPrevious) {
            color = color.tint(0.25).greyScale();
        }
        const allConnectionsSelected = this.getSelectedConnections()
            .map(s => s.source.connection)
            .filter(s => this.connectionTypes.includes(s.info.connectionType as ConnectionType));
        const selection = allConnectionsSelected.map(s => s.connectionId);
        const completenessRecords = completeness
            .filter(c => selection.includes(c.connectionId))
            .filter(c => this.connectionTypes.includes(c.connectionType))
            .flatMap(c => c.meterCompleteness.map(c => c.meterView)
                .filter(m => m));
        const timeRange = isPrevious ? this.comparedTimeRange : this.timeRange;
        return {
            dataset: {
                type: "line",
                borderDash: [5],
                order: 10000,
                yAxisID: "completeness",
                spanGaps: false,
                label: TranslateDirective.getTranslation("Completeness", true),
                borderColor: color.toString(),
                backgroundColor: color.toString(),
                pointBorderColor: color.toString(),
                data: Measurements.getSlots(timeRange)
                    .map(i => {
                        return lodash.round(AppContext.computeCompleteness(completenessRecords, {
                            start: moment(i.startTime).toISOString(),
                            end: moment(i.endTime).toISOString()
                        }, allConnectionsSelected.filter(c => !c.meters.length)), 3);
                    }),
                stack: isPrevious ? DashboardContext.stacks.lastYear : DashboardContext.stacks.currentPeriod,
                tooltip: {
                    stack: isPrevious ? DashboardContext.stacks.lastYear : DashboardContext.stacks.currentPeriod,
                    stackYear: comparedYear,
                    formatter: (ctx: TooltipModel<any>, value: number) => this.chartUtils.percentageFormatter(value),
                    higherValueIsBetter: true
                }
            }
        };
    }

    protected groupDataByMeasurement(data: MeasurementsResult[], dateRange: TimeRange, stack: string, comparedYear: number, estimated: boolean, source: (m: MeasurementsResult) => { [P in DataType]?: AggregatedDataPoint[] }): { [P in DataType]?: MeasurementDataset[] } {
        const measurements: { [P in DataType]?: MeasurementDataset[] } = {};
        data.forEach((m, i) => {
            Object.entries(source(m)).forEach((e) => {
                const measurementType = e[0] as DataType;
                if (!measurements[measurementType]) {
                    measurements[measurementType] = [];
                }
                let dataset: MeasurementDataset;

                if (this.groupByEntityId) {
                    const entities = this.entitiesWithDataType(measurementType, data);
                    const color = entities.length > 1 ?
                        interpolate(entities.indexOf(m.entityId) + 1, 0, entities.length, -0.5, 0.5) : 0;
                    dataset = this.createChartDatasetForEntity(e[1], measurementType, dateRange, stack, m.entityId, color, comparedYear, estimated);
                } else {
                    dataset = this.createChartDataset(e[1], measurementType, dateRange, stack, comparedYear, AppContext.measurementName(measurementType), 0, estimated);
                }
                if (dataset) {
                    measurements[measurementType].push(dataset);
                }
            });
        });
        return measurements;
    }

    private entitiesWithDataType = (measurementType: DataType, data: MeasurementsResult[]): string[] =>
        data.filter(d => d.measurements[measurementType])
            .filter(d => d.measurements[measurementType].some(r => r.value !== 0))
            .map(d => d.entityId);

    private createChartDatasetForEntity(data: AggregatedDataPoint[], measurementType: DataType, dateRange: TimeRange, stack: string, entityId: string, color: number, lastYearNumber?: number, estimated?: boolean): MeasurementDataset {
        const chartDataset = this.createChartDataset(data, measurementType, dateRange, stack,
            lastYearNumber, this.getLabel(measurementType, entityId), color, estimated);
        chartDataset.entityId = entityId;
        chartDataset.dataset.order += color;
        const selectionIds = this.filters.cleanedSources.map(s => s.id);
        chartDataset.isParentMeterData = this.filters.cleanedSources
            .find(s => s.type === EntityType.meter && s.id === chartDataset.entityId && s.source.getChildIds()?.length)
            ?.source.getChildIds().every(i => selectionIds.includes(i));
        return chartDataset.dataset.data.every(d => d === 0) ? null : chartDataset;
    }

    getLabel(measurementType: DataType, entityId?: string) {
        const sourceName = this.filters.allSelectedSources.find(s => s.id === entityId)?.name ?? "";
        return `${AppContext.measurementName(measurementType)}${sourceName ? " - " + sourceName : ""}`;
    }

    createChartDataset(data: AggregatedDataPoint[], measurementType: DataType, dateRange: DashboardTime, stack: string, lastYearNumber?: number, label: string = AppContext.measurementName(measurementType), tint: number = 0, estimated?: boolean): MeasurementDataset {
        const chartColorRgba = DashboardContext.getMeasurementColor(stack, measurementType, tint);
        const groupedData = Measurements.groupByTimeFrames(Measurements.dataPointsToChartData(data), dateRange, AppContext.getAggregationMethod(measurementType));
        const order = AppContext.indexOfDataType(measurementType);
        return {
            measurementType: measurementType,
            dataset: {
                order: estimated ? order * 1000 : order,
                yAxisID: "y1",
                pointBorderColor: "rgb(0,0,0,0)",
                borderColor: chartColorRgba,
                backgroundColor: estimated ? "#FFFFFF" : chartColorRgba,
                borderWidth: estimated ? 1 : 0,
                label: estimated ? `${label} (${TranslateDirective.getTranslation("estimated", true)})` : label,
                data: groupedData.data,
                stack: stack,
                tooltip: {
                    formatter: this.chartUtils.getCustomTooltipFormatter(measurementType),
                    stackYear: lastYearNumber,
                    valuesInverted: AppContext.productionMeasurements.includes(measurementType),
                    labelOverride: "",
                    estimated: estimated
                }
            }
        };
    }

    getChartDataForMeasurement = (measurement: DataType, dateRange: DashboardTime, isPrevious) =>
        this.getChartDataForMeasurements(measurement, dateRange,
            isPrevious ? this._rawPreviousData?.totals : this._rawData?.totals);

    getChartDataForMeasurements = (measurement: DataType, dateRange: DashboardTime, measurements: MeasurementsResult[]) =>
        Measurements.groupByTimeFrames(measurements ? measurements.flatMap(m =>
                Measurements.dataPointsToChartData(m.measurements[measurement])) : [],
            dateRange, AppContext.getAggregationMethod(measurement));

    private getWeatherDataset = (weatherType: DataType, timeRange: DashboardTime, isPrevious: boolean): MeasurementDataset => {
        const stack = isPrevious ? DashboardContext.stacks.lastYear : DashboardContext.stacks.currentPeriod;
        const color = DashboardContext.getMeasurementColor(stack, weatherType);
        return {
            measurementType: weatherType,
            dataset: {
                type: "line",
                order: AppContext.indexOfDataType(weatherType),
                yAxisID: weatherType,
                spanGaps: false,
                borderColor: color,
                backgroundColor: color,
                pointBorderColor: color,
                label: AppContext.measurementName(weatherType),
                data: this.getChartDataForMeasurement(weatherType, timeRange, isPrevious).data,
                stack: stack,
                tooltip: {
                    formatter: this.chartUtils.getCustomTooltipFormatter(weatherType),
                    stackYear: isPrevious ? ChartUtilsService.getComparedYear(this.options.compare, this.timeRange) : null
                }
            }
        };
    }

    private mainConsumptionSetTooltipFormatter(dataset: MeasurementDataset): (ctx: TooltipModel<any>, value: number, index: number) => string {
        return (ctx: TooltipModel<any>, value: number, index: number) =>
            `${this.chartUtils.defaultTooltipFormatter(value, dataset.measurementType)} (Δ ${this.chartUtils.defaultTooltipFormatter(dataset.dataset.data[ctx["dataIndex"]] as number, dataset.measurementType)})`;
    }

    compareIncidents = (a: IncidentOption, b: IncidentOption) => a?.value === b?.value;
}

export interface MeasurementData {
    labels?: string[];
    datasets: MeasurementDataset[];
}

export interface MeasurementDataset<TType extends ChartType = 'line'> {
    measurementType?: DataType;
    entityId?: string;
    estimated?: boolean;
    isParentMeterData?: boolean;
    dataset: ChartDatasetExtended<TType>;
}

export type ChartDatasetExtended<TType extends ChartType = 'line'> = ChartDataset<TType> & {
    measurementType?: DataType;
    buildingType?: BuildingType;
    tooltipLabels?: string[];
    tooltip?: ChartDatasetTooltip;
};

interface ChartDatasetTooltip {
    stack?: string;
    valuesInverted?: boolean;
    higherValueIsBetter?: boolean;
    estimated?: boolean;
    stackYear?: number;
    formatter?: (ctx: TooltipModel<any>, value: number, index: number) => string;
    labelOverride?: string | string[];
    borderColors?: string[];
    data?: number[];
}

export interface ChartCompleteness {
    completeness: number;
    color: string;
    title: string;
}

export interface IncidentOption {
    label: string;
    value: string;
}

export const INCIDENT_OPTIONS: IncidentOption[] = [
    { label: 'Missing data', value: AlertType.MissingData },
    { label: 'Disconnected', value: AlertType.Disconnected },
    { label: 'Contracted power exceeded', value: AlertType.ContractedPowerExceeded },
    { label: 'Contracted feed-in power exceeded', value: AlertType.ContractedFeedInPowerExceeded },
];